import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import SuchAuswahl from "@/components/ui/such-auswahl";
import { Plus, Trash2, ExternalLink, AlertTriangle } from "lucide-react";
import { addTrackerTask, patchTrackerTask, deleteTrackerTask } from "@/lib/api";
import { TASK_PRIORITIES, PRIO_BY_VALUE } from "@/lib/tracker";
import { germanDate } from "@/lib/format";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

export const TaskList = ({ unitId, tasks = [], onChanged }) => {
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ title: "", assigned_to: "", due_date: "", priority: "normal", link: "" });

    const reset = () => setForm({ title: "", assigned_to: "", due_date: "", priority: "normal", link: "" });

    const submit = async () => {
        if (!form.title.trim()) { toast.error("Titel erforderlich"); return; }
        try {
            await addTrackerTask(unitId, {
                title: form.title.trim(),
                assigned_to: form.assigned_to || null,
                due_date: form.due_date || null,
                priority: form.priority || "normal",
                link: form.link || null,
            });
            reset(); setAdding(false);
            onChanged?.();
            toast.success("Aufgabe angelegt");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Aufgabe konnte nicht angelegt werden");
        }
    };

    const toggle = async (t) => {
        try {
            await patchTrackerTask(unitId, t.id, { is_completed: !t.is_completed });
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Aktualisierung fehlgeschlagen");
        }
    };

    const remove = async (t) => {
        if (!window.confirm(`Aufgabe "${t.title}" löschen?`)) return;
        try {
            await deleteTrackerTask(unitId, t.id);
            onChanged?.();
            toast.success("Aufgabe gelöscht");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
        }
    };

    const sortedTasks = [...tasks].sort((a, b) => {
        if (a.is_completed !== b.is_completed) return a.is_completed ? 1 : -1;
        const ad = a.due_date || "9999";
        const bd = b.due_date || "9999";
        return ad.localeCompare(bd);
    });

    return (
        <div className="bg-slate-50/60 border border-slate-200 rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Offene Punkte ({tasks.filter((t) => !t.is_completed).length})</div>
                {!adding && (
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setAdding(true)} data-testid={`add-task-${unitId}`}>
                        <Plus size={11} className="mr-1" /> Aufgabe
                    </Button>
                )}
            </div>

            {adding && (
                <div className="bg-white border border-slate-200 rounded-md p-2 space-y-2">
                    <Input
                        autoFocus
                        placeholder="Aufgabe (z.B. Silikonfugen Bad erneuern)"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        onKeyDown={(e) => e.key === "Enter" && submit()}
                        className="h-8 text-xs"
                        data-testid="task-title-input"
                    />
                    <div className="grid grid-cols-12 gap-2">
                        <Input placeholder="Zuständig (z.B. Hausmeister Müller)" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="col-span-5 h-8 text-xs" />
                        <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="col-span-3 h-8 text-xs" data-testid="task-due-input" />
                        <SuchAuswahl wert={form.priority} onWaehlen={(v) => setForm({ ...form, priority: v })}
                            optionen={[
                                ...TASK_PRIORITIES.map((p) => ({ wert: p.value, text: p.label })),
                            ]}
                            className="col-span-4 text-xs"
                        />
                    </div>
                    <Input
                        placeholder="Link zu Angebot / Auftrag / Rechnung (optional)"
                        value={form.link}
                        onChange={(e) => setForm({ ...form, link: e.target.value })}
                        className="h-8 text-xs"
                        data-testid="task-link-input"
                    />
                    <div className="flex items-center gap-2 justify-end">
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setAdding(false); reset(); }}>Abbrechen</Button>
                        <Button size="sm" className="h-7 text-xs bg-slate-900 hover:bg-slate-800" onClick={submit} data-testid="task-submit">Anlegen</Button>
                    </div>
                </div>
            )}

            {sortedTasks.length === 0 && !adding && (
                <div className="text-xs text-gray-400 italic px-1 py-1">Keine offenen Punkte.</div>
            )}

            {sortedTasks.map((t) => {
                const overdue = !t.is_completed && t.due_date && t.due_date < today();
                const prio = PRIO_BY_VALUE[t.priority] || PRIO_BY_VALUE.normal;
                return (
                    <div
                        key={t.id}
                        className={`flex items-start gap-2 px-2 py-1.5 rounded ${t.is_completed
                            ? "bg-white opacity-50 line-through"
                            : overdue
                                ? "bg-red-50 border border-red-200"
                                : "bg-white border border-gray-100"}`}
                        data-testid={`task-row-${t.id}`}
                    >
                        <Checkbox
                            checked={!!t.is_completed}
                            onCheckedChange={() => toggle(t)}
                            className="mt-0.5"
                            data-testid={`task-toggle-${t.id}`}
                        />
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-slate-900 flex items-center gap-1.5">
                                <span className={`w-1 h-1 rounded-full ${prio.dot}`} />
                                <span className="truncate">{t.title}</span>
                                {overdue && <AlertTriangle size={11} className="text-red-600 shrink-0" />}
                            </div>
                            <div className="text-[10px] text-gray-500 flex items-center gap-2 flex-wrap mt-0.5">
                                {t.assigned_to && <span>{t.assigned_to}</span>}
                                {t.due_date && <span className={overdue ? "text-red-700 font-medium" : ""}>fällig {germanDate(t.due_date)}</span>}
                                {t.link && (
                                    <a href={t.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-sky-700 hover:underline" onClick={(e) => e.stopPropagation()}>
                                        <ExternalLink size={9} /> Link
                                    </a>
                                )}
                            </div>
                        </div>
                        <button type="button" className="text-gray-300 hover:text-red-600" onClick={() => remove(t)} data-testid={`task-delete-${t.id}`}>
                            <Trash2 size={11} />
                        </button>
                    </div>
                );
            })}
        </div>
    );
};

export default TaskList;
